import {useMemo, useState} from "react";
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from "@headlessui/react";
import {
    CheckIcon,
    ChevronUpDownIcon,
    PlusIcon,
    TagIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {fieldLabelClasses, fieldVariants} from "../../general/fieldStyles";
import {cn} from "../../../lib/utils";

export interface Subledger {
    id: string;
    name: string;
}

export const NO_SUBLEDGER_LABEL = "Zonder subledger";

interface CreateSubledgerOption {
    id: "create-subledger";
    name: string;
    create: true;
}

type SubledgerOption = Subledger | CreateSubledgerOption;

interface SubledgerSelectorProps {
    subledgers: Subledger[];
    value: Subledger | null;
    onChange: (subledger: Subledger | null) => void;
    onCreate: (name: string) => void;
    disabled?: boolean;
    inputId?: string;
    optionsPlacement?: "top" | "bottom";
}

function normalizeName(name: string): string {
    return name.trim().toLocaleLowerCase("nl-NL");
}

export function SubledgerSelector({
    subledgers,
    value,
    onChange,
    onCreate,
    disabled = false,
    inputId = "subledger",
    optionsPlacement = "bottom",
}: SubledgerSelectorProps) {
    const [query, setQuery] = useState("");
    const normalizedQuery = normalizeName(query);

    const filteredSubledgers = useMemo(() => {
        if (!normalizedQuery) return subledgers;
        return subledgers.filter((subledger) => normalizeName(subledger.name).includes(normalizedQuery));
    }, [normalizedQuery, subledgers]);

    const canCreate =
        normalizedQuery.length > 0 &&
        !subledgers.some((subledger) => normalizeName(subledger.name) === normalizedQuery);

    const createOption: CreateSubledgerOption | null = canCreate
        ? {id: "create-subledger", name: query.trim(), create: true}
        : null;

    function handleChange(option: SubledgerOption | null) {
        setQuery("");
        if (!option) {
            onChange(null);
            return;
        }
        if ("create" in option) {
            onCreate(option.name);
            return;
        }
        onChange(option);
    }

    return (
        <div>
            <div className="mb-2 flex items-baseline justify-between gap-3">
                <label htmlFor={inputId} className={fieldLabelClasses}>
                    Subledger
                </label>
                <span className="text-xs text-gray-400">Optioneel</span>
            </div>
            <Combobox
                value={value}
                by="id"
                immediate
                disabled={disabled}
                onChange={handleChange}
                onClose={() => setQuery("")}
                as="div"
                className="relative w-full"
            >
                <div className="relative">
                    <TagIcon
                        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
                        aria-hidden="true"
                    />
                    <ComboboxInput
                        id={inputId}
                        aria-label="Subledger"
                        placeholder="Zoek of maak een subledger"
                        displayValue={(subledger: Subledger | null) => subledger?.name ?? ""}
                        onChange={(event) => setQuery(event.target.value)}
                        className={cn(fieldVariants(), "pl-9 pr-20")}
                    />
                    {value && !disabled && (
                        <button
                            type="button"
                            onClick={() => handleChange(null)}
                            className="absolute inset-y-0 right-9 flex items-center px-2 text-gray-400 hover:text-gray-600"
                            aria-label="Subledger wissen"
                        >
                            <XMarkIcon className="size-4" aria-hidden="true" />
                        </button>
                    )}
                    <ComboboxButton
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
                        aria-label="Subledgers tonen"
                    >
                        <ChevronUpDownIcon className="size-5" aria-hidden="true" />
                    </ComboboxButton>
                </div>

                <ComboboxOptions
                    className={cn(
                        "absolute z-50 max-h-64 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none empty:hidden",
                        optionsPlacement === "top" ? "bottom-full mb-1" : "mt-1"
                    )}
                >
                    {filteredSubledgers.map((subledger) => (
                        <ComboboxOption
                            key={subledger.id}
                            value={subledger}
                            className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-gray-900 data-[focus]:bg-gray-50"
                        >
                            <TagIcon className="size-4 shrink-0 text-gray-400" aria-hidden="true" />
                            <span className="min-w-0 flex-1 truncate group-data-[selected]:font-semibold">
                                {subledger.name}
                            </span>
                            <CheckIcon
                                className="hidden size-4 shrink-0 text-[color:var(--primary)] group-data-[selected]:block"
                                aria-hidden="true"
                            />
                        </ComboboxOption>
                    ))}

                    {createOption && (
                        <ComboboxOption
                            value={createOption}
                            className="group flex cursor-pointer items-center gap-2 border-t border-gray-100 px-3 py-2 text-[color:var(--primary)] data-[focus]:bg-gray-50"
                        >
                            <PlusIcon className="size-4 shrink-0" aria-hidden="true" />
                            <span className="min-w-0 truncate">
                                <span className="font-medium">'{createOption.name}'</span> aanmaken
                            </span>
                        </ComboboxOption>
                    )}

                    {filteredSubledgers.length === 0 && !createOption && (
                        <div className="px-3 py-3 text-center text-gray-400">
                            Geen subledgers gevonden
                        </div>
                    )}
                </ComboboxOptions>
            </Combobox>
        </div>
    );
}
